/* global rwgps */
import React from 'react'
import styles from './Metrics.scss'
import { formatDistance, formatSpeed, formatGrade, formatDuration, formatPace } from 'lib/units'
import { getConsole } from 'lib/getConsole'
import { useFormatMessage, commonMetrics } from 'lib/localization'
import { OrderedMap } from 'immutable'
import moment from 'moment'
import _ from 'lodash'
const console = getConsole('common', 'Metrics')

const formatTime = (timestampSeconds) => {
  const m = moment(timestampSeconds * 1000)
  const val = m.format('h:mm')
  const amPm = m.format('a')
  return {
    valueToString: () => val,
    short: amPm,
    long: amPm
  }
}

const formatVAM = (vam) => ({
  short: '',
  long: 'Vm/h',
  valueToString: () => vam
})

const tripMetrics = {
  minSpeed: {
    label: commonMetrics.minSpeed,
    value: (mcResult, metricUnits) => formatSpeed(mcResult.speed.min, { metricUnits })
  },
  maxSpeed: {
    label: commonMetrics.maxSpeed,
    value: (mcResult, metricUnits) => formatSpeed(mcResult.speed.max, { metricUnits })
  },
  avgSpeed: {
    label: commonMetrics.avgSpeed,
    value: (mcResult, metricUnits) => formatSpeed(mcResult.speed.avg, { metricUnits })
  },
  duration: {
    label: commonMetrics.duration,
    value: (mcResult, metricUnits) => formatDuration(mcResult.duration)
  },
  startTime: {
    label: commonMetrics.startTime,
    value: (mcResult, metricUnits) => formatTime(mcResult.firstTime)
  },
  endTime: {
    label: commonMetrics.endTime,
    value: (mcResult, metricUnits) => formatTime(mcResult.firstTime + mcResult.duration)
  },
  movingTime: {
    label: commonMetrics.movingTime,
    value: (mcResult, metricUnits) => formatDuration(mcResult.movingTime)
  },
  stoppedTime: {
    label: commonMetrics.stoppedTime,
    value: (mcResult, metricUnits) => formatDuration(mcResult.stoppedTime)
  },
  pace: {
    label: commonMetrics.pace,
    value: (mcResult, metricUnits) => formatPace(mcResult.pace, { metricUnits })
  },
  movingPace: {
    label: commonMetrics.movingPace,
    value: (mcResult, metricUnits) => formatPace(mcResult.movingPace, { metricUnits })
  },
  ascentTime: {
    label: commonMetrics.ascentTime,
    value: (mcResult, metricUnits) => formatDuration(mcResult.ascentTime)
  },
  descentTime: {
    label: commonMetrics.descentTime,
    value: (mcResult, metricUnits) => formatDuration(mcResult.descentTime)
  },
  vam: {
    label: commonMetrics.vam,
    value: (mcResult, metricUnits) => formatVAM(mcResult.vam.toFixed(0))
  }
}

const trouteMetrics = {
  minEle: {
    label: commonMetrics.minElevation,
    value: (mcResult, metricUnits) => formatDistance(mcResult.ele.min, { metricUnits, smallUnits: true })
  },
  maxEle: {
    label: commonMetrics.maxElevation,
    value: (mcResult, metricUnits) => formatDistance(mcResult.ele.max, { metricUnits, smallUnits: true })
  },
  avgEle: {
    label: commonMetrics.avgElevation,
    value: (mcResult, metricUnits) => formatDistance(mcResult.ele.avg, { metricUnits, smallUnits: true })
  },
  minGrade: {
    label: commonMetrics.minGrade,
    value: (mcResult, metricUnits) => formatGrade(mcResult.grade.min)
  },
  maxGrade: {
    label: commonMetrics.maxGrade,
    value: (mcResult, metricUnits) => formatGrade(mcResult.grade.max)
  },
  avgGrade: {
    label: commonMetrics.avgGrade,
    value: (mcResult, metricUnits) => formatGrade(mcResult.grade.avg)
  },
  distance: {
    label: commonMetrics.distance,
    value: (mcResult, metricUnits) => formatDistance(mcResult.distance, { metricUnits })
  },
  startEle: {
    label: commonMetrics.startElevation,
    value: (mcResult, metricUnits) => formatDistance(mcResult.startElevation, { metricUnits, smallUnits: true })
  },
  endEle: {
    label: commonMetrics.endElevation,
    value: (mcResult, metricUnits) => formatDistance(mcResult.endElevation, { metricUnits, smallUnits: true })
  },
  eleGain: {
    label: commonMetrics.elevationGain,
    value: (mcResult, metricUnits) => formatDistance(mcResult.ele_gain, { metricUnits, smallUnits: true })
  },
  eleLoss: {
    label: commonMetrics.elevationLoss,
    value: (mcResult, metricUnits) => formatDistance(mcResult.ele_loss, { metricUnits, smallUnits: true })
  }
}

export const metricsSpec = OrderedMap({ ...tripMetrics, ...trouteMetrics })

export const routeMetricsSpec = OrderedMap({ ...trouteMetrics })

// Metrics display component, will probably be its own blocktype as well at some
// point so I'm trying to not couple this to the Map tightly
export class Metrics extends React.PureComponent {
  render () {
    const { ride, highlightSlice, metricsToShow, metricUnits } = this.props
    // keeping this around so that we get lots of warning if something causes
    // this (comparatively expensive) re-render
    console.log(`⚠️ recalculated metrics (ride id = ${ride.get('id')})`)
    const tpsResult = rwgps.TrackPoint.fromJSON(ride.rawPoints)
    const points = tpsResult.trackPoints
    const data = new rwgps.Data(points)
    const dataSlice = highlightSlice && data.slice(highlightSlice[0], highlightSlice[1] - highlightSlice[0])

    const toCalculate = highlightSlice
      ? dataSlice
      : data

    // in our case, setting readonly to false can't cause problems here because
    // the source data is immutable
    const mc = new rwgps.MetricsCalculator(toCalculate, false)
    const mcResult = mc.calculate()

    return (
      <div className={styles.Metrics}>
        {metricsToShow.map(k => (
          <Metric metric={metricsSpec.get(k)} metricUnits={metricUnits} calculatedMetrics={mcResult} key={k} />
        ))}
      </div>
    )
  }
}

// // temp hack
// export class Metrics extends React.PureComponent {
//   shouldComponentUpdate (nextProps) {
//     const props = this.props
//     console.log('shouldComponentUpdate metrics')
//     console.log('nextProps.ride === props.ride', nextProps.ride === props.ride)
//     console.log('nextProps.highlightSlice === props.highlightSlice', nextProps.highlightSlice === props.highlightSlice)
//     // metricsToShow is the culprit
//     console.log('nextProps.metricsToShow === props.metricsToShow', nextProps.metricsToShow === props.metricsToShow)
//   }

//   render () {
//     return Metrics_(this.props)
//   }
// }

const Metric = ({ metric, calculatedMetrics, metricUnits }) => {
  const formatted = metric.value(calculatedMetrics, metricUnits)
  const fm = useFormatMessage()
  return (
    <div className={styles.Metric}>
      <h4>{_.startCase(fm(metric.label))}</h4>
      <p><span className={styles.metricValue}>{formatted.valueToString()}</span> <span className={styles.metricUnits}>{formatted.short}</span></p>
    </div>
  )
}
